import { useId, useRef, useState, type ReactNode } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import styles from './ExpandableCard.module.css';

interface Props {
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  trailing?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
}

/** Shared disclosure: native keyboard controls, animated content and an inert closed panel. */
export function ExpandableCard({ title, description, icon, trailing, children, defaultOpen = false }: Props) {
  const [expanded, setExpanded] = useState(defaultOpen);
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  return <section className={styles.card} data-expanded={expanded}>
    <button ref={trigger} type="button" id={`${id}-trigger`} className={styles.trigger} aria-expanded={expanded} aria-controls={`${id}-panel`} onClick={() => setExpanded(value => !value)}>
      {icon && <span className={styles.icon} aria-hidden>{icon}</span>}
      <span className={styles.heading}><span className={styles.title}>{title}</span>{description && <span className={styles.description}>{description}</span>}</span>
      {trailing && <span className={styles.trailing}>{trailing}</span>}
      <CaretDown className={styles.chevron} size={20} weight="bold" aria-hidden />
    </button>
    <div id={`${id}-panel`} role="region" aria-labelledby={`${id}-trigger`} aria-hidden={!expanded} className={styles.panel} ref={node => {
      if (!node) return;
      if (!expanded && node.contains(document.activeElement)) trigger.current?.focus();
      node.inert = !expanded;
    }}>
      <div className={styles.clip}><div className={styles.content}>{children}</div></div>
    </div>
  </section>;
}
